/**
 * Command River — Dispatch Chronograph craft.
 *
 * Signature: night-watch chronograph — phosphor amber, traveling stage bead,
 * T+ elapsed, risk dial, correlator teletype. Not a Cameras Live twin.
 *
 * Soft≠Live: Soft fixtures stamped soft_demo; Soft Advance never writes Live.
 * Soft invent Live writes forbidden
 */
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { AnimatePresence } from "framer-motion";
import { html, M, Alert, SOFT, LIFT, go, stop, hashSearchParams } from "./ui.js";
import { path, useApi, useAction } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { useEventsLive } from "./stream-live.js";
import { dispatchRowsOf, softResponseErrorKind, softResponseErrorOf } from "./response-live.js";
import { COLS, BOARD_META, responseBoardSeatOf } from "./response-board.js";
import { isLocalHost } from "./pathseal.js";
import { boardHeadingOf, operatorSay } from "./operator-say.js";
import {
  softQueueRowsOf,
  softSyncReset,
  softSyncSubscribe,
  softQueueAdvance,
} from "./response-soft-sync.js";

function safeBoardPath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    if (op === "get__response_board_status") return "/response/board/status";
    if (op === "get__responder-dispatches") {
      const lim = (opts && opts.query && opts.query.limit) || 64;
      return `/responder-dispatches?limit=${encodeURIComponent(lim)}`;
    }
    if (op === "get__responder-dispatches_mine") {
      const lim = (opts && opts.query && opts.query.limit) || 64;
      return `/responder-dispatches/mine?limit=${encodeURIComponent(lim)}`;
    }
    if (op === "post__responder-dispatches_p_transition") {
      const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
      return `/responder-dispatches/${encodeURIComponent(id)}/transition`;
    }
    if (op === "post__responder-dispatches_mine_p_transition") {
      const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
      return `/responder-dispatches/mine/${encodeURIComponent(id)}/transition`;
    }
    return "";
  }
}

const TAP = { scale: 0.97 };

function priorityOf(row) {
  const r = Number(row && row.risk) || 0;
  if (r >= 80) return { band: "p1", label: "P1", tone: "critical" };
  if (r >= 60) return { band: "p2", label: "P2", tone: "high" };
  if (r >= 30) return { band: "p3", label: "P3", tone: "watch" };
  return { band: "p4", label: "P4", tone: "low" };
}

function sortQueue(rows) {
  return [...rows].sort((a, b) => {
    const ra = Number(a.risk) || 0;
    const rb = Number(b.risk) || 0;
    if (rb !== ra) return rb - ra;
    return String(a.id).localeCompare(String(b.id));
  });
}

function fmtAge(sec) {
  const s = Math.max(0, Math.floor(Number(sec) || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `T+${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

function fmtEta(min) {
  if (min == null || !Number.isFinite(Number(min))) return "—";
  const n = Math.max(0, Math.floor(Number(min)));
  return n === 0 ? "ON SITE" : `ETA ${n}m`;
}

function stageLabel(status) {
  const meta = BOARD_META[status];
  return meta ? meta[0] : operatorSay(status, String(status || "—").replace("_", " "));
}

/** Compact stage filter — lives inside Queue (one place, not a second dashboard). */
function QueueStages({ status, stageOnly, counts, onPick }) {
  const idx = Math.max(0, COLS.indexOf(status));
  return html`
    <div className="rr-queue__stages" role="toolbar" aria-label="Filter by stage">
      <button type="button" className="rr-queue__stage" data-on=${!stageOnly ? "1" : "0"}
        onClick=${() => onPick("")}>
        <span>All</span><b>${COLS.reduce((n, c) => n + (c === "resolved" ? 0 : counts(c)), 0)}</b>
      </button>
      ${COLS.filter((c) => c !== "resolved").map((c, i) => html`
        <button key=${c} type="button" className="rr-queue__stage"
          data-on=${stageOnly === c ? "1" : "0"}
          data-now=${i === idx ? "1" : "0"}
          title=${BOARD_META[c][1]}
          onClick=${() => onPick(c)}>
          <span>${BOARD_META[c][0]}</span><b>${counts(c)}</b>
        </button>`)}
    </div>
  `;
}

/** Per-job stage ribbon — shows how far this send has traveled. */
function QueuePath({ status }) {
  const idx = Math.max(0, COLS.indexOf(status));
  return html`
    <ol className="rr-queue__path" aria-label=${"Stage " + stageLabel(status)}>
      ${COLS.map((c, i) => html`
        <li key=${c}
          data-done=${i < idx ? "1" : "0"}
          data-now=${i === idx ? "1" : "0"}
          title=${BOARD_META[c][0]}/>`)}
    </ol>
  `;
}

function RiskDial({ risk }) {
  const r = Math.max(0, Math.min(100, Number(risk) || 0));
  const C = 2 * Math.PI * 34;
  const dash = (r / 100) * C;
  const tone = r >= 80 ? "p1" : r >= 60 ? "p2" : r >= 30 ? "p3" : "p4";
  return html`
    <div className="rr-dial" data-tone=${tone} aria-label=${"Risk " + r}>
      <svg viewBox="0 0 80 80" width="56" height="56" aria-hidden="true">
        <circle cx="40" cy="40" r="34" className="rr-dial__track"/>
        <circle cx="40" cy="40" r="34" className="rr-dial__arc"
          strokeDasharray=${dash + " " + C}
          transform="rotate(-90 40 40)"/>
      </svg>
      <div className="rr-dial__read">
        <b>${r}</b>
        <small>RISK</small>
      </div>
    </div>
  `;
}

function useTickAge(baseAge, active) {
  const [age, setAge] = useState(baseAge || 0);
  const start = useRef(Date.now());
  const base = useRef(baseAge || 0);
  useEffect(() => {
    base.current = Number(baseAge) || 0;
    start.current = Date.now();
    setAge(base.current);
  }, [baseAge, active]);
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => {
      setAge(base.current + Math.floor((Date.now() - start.current) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [active]);
  return age;
}

export function DispatchArc({ toast, shellSeat, onOpenCase }) {
  const posture = useApi(safeBoardPath("get__response_board_status"));
  const seat = responseBoardSeatOf(posture.loading ? null : (posture.data || {}));
  const busMode = seat.teamListAllowed ? "team" : seat.mineListAllowed ? "mine" : "none";
  const listPath =
    busMode === "team"
      ? safeBoardPath("get__responder-dispatches", { query: { limit: seat.listLimit } })
      : busMode === "mine"
        ? safeBoardPath("get__responder-dispatches_mine", { query: { limit: seat.listLimit } })
        : "";
  const live = useApi(listPath);
  const dispatchEvents = useEventsLive(15000);
  const liveList = useMemo(() => {
    return dispatchRowsOf(live.data).map((r) => ({
      ...r,
      callsign: (r.raw && (r.raw.callsign || r.raw.unit_callsign)) || "—",
      channel: (r.raw && r.raw.channel) || "—",
      zone: (r.raw && (r.raw.zone_label || r.raw.zone)) || "",
      aiNote: "",
      etaMin: null,
      ageSec: 0,
    }));
  }, [live.data]);
  const [softRows, setSoftRows] = useState(() => softQueueRowsOf());
  const [forceSoft, setForceSoft] = useState(false);
  const [focusId, setFocusId] = useState("");
  const [stageOnly, setStageOnly] = useState("");
  const [advFlash, setAdvFlash] = useState(0);
  const [howtoOn, setHowtoOn] = useState(() => {
    try { return localStorage.getItem("rr-howto-off") !== "1"; } catch { return true; }
  });
  const act = useAction();

  useEffect(() => softSyncSubscribe((snap) => {
    if (snap && Array.isArray(snap.queue)) {
      setSoftRows(snap.queue.map((r) => ({ ...r })));
    }
  }), []);

  const newestDispatchEventId = useMemo(() => {
    const rows = dispatchEvents.data && Array.isArray(dispatchEvents.data.items)
      ? dispatchEvents.data.items
      : [];
    const hit = rows.find((ev) =>
      String(ev && ev.event_type || "").startsWith("responder.dispatch."),
    );
    return hit ? String(hit.id || hit.event_id || hit.ts_epoch_ms || "") : "";
  }, [dispatchEvents.data]);
  const seenDispatchEvent = useRef("");
  useEffect(() => {
    if (!newestDispatchEventId || newestDispatchEventId === seenDispatchEvent.current) return;
    seenDispatchEvent.current = newestDispatchEventId;
    if (!forceSoft) live.reload && live.reload();
  }, [newestDispatchEventId, forceSoft, live.reload]);

  useEffect(() => {
    let sp = null;
    try { sp = hashSearchParams(); } catch { sp = null; }
    const focus = sp && sp.get ? String(sp.get("focus") || "").trim() : "";
    if (focus) setFocusId(focus);
  }, []);

  const useSoft = isLocalHost() && forceSoft;
  const list = useSoft ? softRows : liveList;
  const softPreview = useSoft;

  const visible = stageOnly
    ? list.filter((x) => x.status === stageOnly)
    : list.filter((x) => x.status !== "resolved");
  const queue = useMemo(() => sortQueue(visible.length ? visible : list), [visible, list]);
  const focus = list.find((x) => x.id === focusId)
    || queue.find((x) => !["resolved", "cancelled"].includes(x.status))
    || queue[0]
    || null;

  useEffect(() => {
    if (!focus) return;
    if (!focusId || !list.some((x) => x.id === focusId)) setFocusId(focus.id);
  }, [list, focusId, focus]);

  const tickAge = useTickAge(focus && focus.ageSec, !!focus);
  const canWriteLive =
    (busMode === "team" && seat.teamTransitionAllowed)
    || (busMode === "mine" && seat.mineTransitionAllowed);
  const canAdvance = softPreview || canWriteLive;
  const count = (c) => list.filter((x) => x.status === c).length;
  const nextOf = (s) => COLS[COLS.indexOf(s) + 1];
  const activeN = list.filter((x) => !["resolved", "cancelled"].includes(x.status)).length;
  const riskN = list.filter((x) => x.risk >= 60).length;
  const p1N = list.filter((x) => (Number(x.risk) || 0) >= 80).length;

  const spinBy = useCallback((delta) => {
    if (!queue.length || !focus) return;
    const i = Math.max(0, queue.findIndex((x) => x.id === focus.id));
    const next = (i + delta + queue.length) % queue.length;
    setFocusId(queue[next].id);
  }, [queue, focus]);

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName) || "";
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (e.key === "ArrowUp" || e.key === "ArrowLeft") { e.preventDefault(); spinBy(-1); }
      if (e.key === "ArrowDown" || e.key === "ArrowRight") { e.preventDefault(); spinBy(1); }
      if ((e.key === "a" || e.key === "A") && focus) {
        e.preventDefault();
        document.getElementById("rr-advance-btn")?.click();
      }
      if (e.key === "Enter" && focus && focus.incident && onOpenCase) {
        e.preventDefault();
        onOpenCase(focus.incident);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [spinBy, focus, onOpenCase]);

  const advance = async () => {
    if (!focus) return;
    const to = nextOf(focus.status);
    if (!to) return;
    if (softPreview || focus.soft || focus.soft_demo) {
      softQueueAdvance(focus.id, to);
      setAdvFlash((n) => n + 1);
      toast && toast("Soft only → " + operatorSay(to, to.replace("_", " ")) + " · not Live", "warn");
      return;
    }
    if (!canWriteLive) {
      toast && toast("You cannot change dispatch status on this seat", "warn");
      return;
    }
    const op = busMode === "mine"
      ? "post__responder-dispatches_mine_p_transition"
      : "post__responder-dispatches_p_transition";
    const out = await act.run(
      safeBoardPath(op, { pathParams: [focus.id] }),
      { method: "POST", body: { status: to } },
    );
    if (out) {
      setAdvFlash((n) => n + 1);
      toast && toast("Dispatch → " + operatorSay(to, to.replace("_", " ")), "go");
      live.reload && live.reload();
    } else {
      toast && toast(softResponseErrorOf(act.error || "Transition refused"), "bad");
    }
  };

  const pickStage = (c) => setStageOnly((cur) => (cur === c ? "" : c));
  const pri = focus ? priorityOf(focus) : null;
  const next = focus ? nextOf(focus.status) : null;
  const openMission = () => {
    if (focus && focus.incident && onOpenCase) {
      if (softPreview || focus.soft_demo) {
        toast && toast("Practice Mission Room · local preview only", "warn");
      }
      onOpenCase(focus.incident);
      return;
    }
    go("/response/cases");
  };

  return html`
    <div className="rr-desk" data-cmc-board-ux="rsp-board-v2" data-bus=${busMode}
      data-soft-demo=${softPreview ? "1" : "0"} data-craft="chronograph">
      ${act.envelope
        ? html`<p className="note" data-cmc-last-action="1">Last action · ${envelopeSummary(act.envelope) || "—"}</p>`
        : null}
      ${!posture.loading && !seat.enabled && !softPreview
        ? html`<${Alert} tone="warn" title="Board seat closed" say=${softResponseErrorOf(seat.detail || "Board closed")}/>`
        : null}
      ${live.error && !softPreview
        ? html`<${Alert} tone="warn"
            title=${(softResponseErrorKind(live.error) === "site" || softResponseErrorKind(live.error) === "sa_site")
              ? "Site scope required"
              : "Could not refresh dispatches"}
            say=${softResponseErrorOf(live.error)}/>`
        : null}

      <div className="rr-mast" role="status" data-tone=${softPreview ? "soft" : "live"}>
        <div className="rr-mast__left">
          ${softPreview
            ? html`<b>Practice</b>
                <span>Safe demo — clicks stay on this screen</span>
                <span className="rr-mast__legal">Practice writes stay on this screen.</span>`
            : html`<b>Live</b><span>${liveList.length} real jobs · ${busMode === "none" ? "read only" : busMode === "mine" ? "your jobs" : "the site queue"}</span>`}
        </div>
        <div className="rr-mast__stats" aria-label="Queue pulse">
          <span><i>Open</i><b>${activeN}</b></span>
          <span data-tone=${p1N ? "bad" : ""}><i>Urgent</i><b>${p1N}</b></span>
          <span data-tone=${riskN ? "warn" : ""}><i>High risk</i><b>${riskN}</b></span>
        </div>
        <div className="rr-mast__acts">
          ${softPreview && isLocalHost() ? html`
            ${liveList.length > 0 ? html`
              <button type="button" onClick=${() => setForceSoft(false)}>Use Live (${liveList.length})</button>` : null}
            <button type="button" onClick=${() => {
              softSyncReset();
              setForceSoft(true);
              setFocusId("");
            }}>Reset demo</button>
          ` : isLocalHost() ? html`
            <button type="button" onClick=${() => setForceSoft(true)}>Try practice</button>
          ` : null}
        </div>
      </div>

      ${howtoOn ? html`
        <div className="rr-howto" role="note">
          <ol>
            <li><b>1</b><span>Queue: click a job, push its stage</span></li>
            <li><b>2</b><span>Open Case file for that same incident</span></li>
            <li><b>3</b><span>Dispatched phones send photo/clip + reason onto this Case</span></li>
          </ol>
          <button type="button" className="rr-howto__x" aria-label="Hide how to use"
            onClick=${() => {
              setHowtoOn(false);
              try { localStorage.setItem("rr-howto-off", "1"); } catch { /* ignore */ }
            }}>Got it</button>
        </div>` : null}

      <div className="rr-band--work">
        <section className="rr-queue" aria-label="Send queue" data-craft="strip-v2">
          <header className="rr-queue__hd">
            <div className="rr-queue__hd-copy">
              <b>${softPreview ? "Practice send queue" : "Send queue"}</b>
              <span>${queue.length} open · risk first · click a strip to command</span>
            </div>
            <div className="rr-keys" aria-hidden="true">
              <kbd>↑↓</kbd><span>select</span>
              <kbd>A</kbd><span>push</span>
            </div>
          </header>

          <div className="rr-queue__tools">
            <${QueueStages}
              status=${focus ? focus.status : ""}
              stageOnly=${stageOnly}
              counts=${count}
              onPick=${pickStage}
            />
            ${stageOnly ? html`
              <p className="rr-queue__filter">
                Showing <b>${stageLabel(stageOnly)}</b> only
                <button type="button" onClick=${() => setStageOnly("")}>Clear</button>
              </p>` : null}
          </div>

          ${queue.length === 0
            ? html`<p className="rr-queue__empty">${live.loading || posture.loading
              ? "Loading sends…"
              : live.error && !softPreview
                ? "Live queue did not load. Practice rows stay off this list."
                : (stageOnly
                  ? ("No " + stageLabel(stageOnly) + " jobs — clear the filter or wait for Map.")
                  : "No live dispatches in this site yet.")}</p>`
            : html`<ul className="rr-queue__list" role="listbox">
                ${queue.map((row, i) => {
                  const p = priorityOf(row);
                  const on = focus && focus.id === row.id;
                  const st = stageLabel(row.status);
                  return html`<${M.li} key=${row.id} role="option"
                    aria-selected=${on ? "true" : "false"}
                    data-on=${on ? "1" : "0"}
                    data-band=${p.band}
                    data-soft=${row.soft_demo ? "1" : "0"}
                    initial=${{ opacity: 0, y: 6 }}
                    animate=${{ opacity: 1, y: 0 }}
                    transition=${{ ...SOFT, delay: Math.min(i * 0.028, 0.18) }}
                    onClick=${() => setFocusId(row.id)}>
                    <i className="rr-queue__rail" aria-hidden="true"/>
                    <div className="rr-queue__rank" aria-hidden="true">${String(i + 1).padStart(2, "0")}</div>
                    <div className="rr-queue__main">
                      <div className="rr-queue__title-row">
                        <strong>${boardHeadingOf(row.devLabel)}</strong>
                        <abbr title=${"Risk " + row.risk} data-band=${p.band}>${p.label}</abbr>
                      </div>
                      <p className="rr-queue__why">${row.reason || row.aiNote || "—"}</p>
                      <div className="rr-queue__meta">
                        <span data-k="unit">${row.callsign && row.callsign !== "—" ? row.callsign : "Unassigned"}</span>
                        <span>${row.who && row.who !== "—" ? row.who : "No name"}</span>
                        <span>${fmtAge(row.ageSec || 0)}</span>
                      </div>
                      <${QueuePath} status=${row.status}/>
                    </div>
                    <div className="rr-queue__side">
                      <b data-stage=${row.status}>${st}</b>
                      <small>${fmtEta(row.etaMin)}</small>
                      ${on ? html`<em>Commanding</em>` : null}
                    </div>
                  </${M.li}>`;
                })}
              </ul>`}
        </section>

        <section className="rr-now" aria-live="polite" data-flash=${advFlash}>
          <${AnimatePresence} mode="wait">
            ${!focus
              ? html`<${M.div} key="empty" className="rr-now__body"
                  initial=${{ opacity: 0, y: 8 }} animate=${{ opacity: 1, y: 0 }}
                  exit=${{ opacity: 0 }} transition=${SOFT}>
                  <p className="rr-now__lede">Click a job on the left to take command.</p>
                  <p className="rr-now__lede rr-now__lede--sub">Then push its stage, or open Mission Room for proof.</p>
                </${M.div}>`
              : html`<${M.div} key=${focus.id} className="rr-now__stack"
                  initial=${{ opacity: 0, y: 10 }}
                  animate=${{ opacity: 1, y: 0 }}
                  exit=${{ opacity: 0, y: -6 }} transition=${SOFT}>
                  <div className="rr-now__body">
                    <div className="rr-now__top">
                      <div>
                        <div className="rr-now__kicker">
                          <abbr data-band=${pri.band}>${pri.label}</abbr>
                          <span>${operatorSay(focus.status, String(focus.status).replace("_", " "))}</span>
                          ${focus.soft_demo ? html`<em>Practice</em>` : null}
                        </div>
                        <h2 className="rr-now__title">${boardHeadingOf(focus.devLabel)}</h2>
                        <p className="rr-now__zone">${focus.zone || "Zone —"} · ${focus.correlation || "—"}</p>
                      </div>
                      <${RiskDial} risk=${focus.risk}/>
                    </div>

                    <div className="rr-now__meters">
                      <div>
                        <small>Elapsed</small>
                        <b className="rr-now__clock">${fmtAge(tickAge)}</b>
                      </div>
                      <div>
                        <small>Unit</small>
                        <b>${focus.callsign || "—"}</b>
                      </div>
                      <div>
                        <small>Channel</small>
                        <b>${focus.channel || "—"}</b>
                      </div>
                      <div>
                        <small>ETA</small>
                        <b>${fmtEta(focus.etaMin)}</b>
                      </div>
                    </div>

                    <p className="rr-now__why">${operatorSay(focus.reason, "On this run")}</p>
                    ${focus.aiNote ? html`
                      <blockquote className="rr-now__ai">
                        <span>AI counsel</span>
                        <p>${focus.aiNote}</p>
                      </blockquote>` : null}
                  </div>

                  <div className="rr-now__dock">
                    <p className="rr-now__dock-lab">Your move</p>
                    <div className="rr-now__cta">
                      ${canAdvance && next ? html`
                        <${M.button} id="rr-advance-btn" type="button" className="btn btn--go rr-now__adv"
                          whileHover=${{ scale: 1.02 }} whileTap=${TAP} transition=${LIFT}
                          disabled=${act.busy} onClick=${(e) => { stop(e); advance(); }}>
                          Push to ${BOARD_META[next][0]}
                          <kbd>A</kbd>
                        </${M.button}>` : null}
                      <${M.button} type="button" className="btn btn--ghost"
                        whileHover=${{ scale: 1.02 }} whileTap=${TAP} transition=${LIFT}
                        onClick=${openMission}>
                        Open Mission Room
                      </${M.button}>
                    </div>
                    <div className="rr-now__note">
                      <small>${next ? "What Push does" : "When to open Case file"}</small>
                      <p>${next
                        ? (BOARD_META[next][0] + " — " + BOARD_META[next][1])
                        : "Open Mission Room / Case file — desk receives phone uploads from the dispatched unit."}</p>
                    </div>
                  </div>
                </${M.div}>`}
          </${AnimatePresence}>
        </section>
      </div>
    </div>
  `;
}

export function DispatchGyro(props) {
  return DispatchArc(props);
}

export default DispatchArc;
